import { Fragment, FC, useMemo } from 'react';
import { useFragment, graphql } from 'react-relay';

import { SeoLink } from 'dibs-elements/exports/SeoLink';
import { LazyImage, LazyImageProps } from '../../../LazyImage/LazyImage';
import { ImageSizeType } from '../../../types/StyleSizes';
import styles from '../../styles.scss';

import { getPhotoImgProps } from './helpers';
import { useAppliedFilterAltText } from '../../../helpers/useValidFilterHelpers';
import { useValidFilter } from '../../../helpers/useValidFilter';
import { LazyThumbVideo } from './LazyThumbVideo';

import { SwiperItem_photo$key } from './__generated__/SwiperItem_photo.graphql';
import { SwiperItem_viewer$key } from './__generated__/SwiperItem_viewer.graphql';
import { SwiperItem_itemSearch$key } from './__generated__/SwiperItem_itemSearch.graphql';

export interface ItemProps {
    disableLinks?: boolean;
    onClick?: () => void;
    pdpUrl: string;
    title: string;
    containerClassName?: string;
    srcSetSizes?: string;
    srcSetWidths?: Array<number>;
    srcSetQuality?: number;
    useLoFiLazyLoader: boolean;
    imageSize: ImageSizeType;
    imageLoadVerticalOffset?: number;
    onContentLoaded?: () => void;
}
interface Props extends ItemProps {
    index: number;
    useEagerImageLoad: boolean;
    photo: SwiperItem_photo$key;
    itemSearch: SwiperItem_itemSearch$key | null;
    viewer?: SwiperItem_viewer$key | null;
    itemId?: string | null;
}

const photoFragment = graphql`
    fragment SwiperItem_photo on ItemPhoto {
        placeholder
        smallPath
        masterOrZoomPath
    }
`;

const viewerFragment = graphql`
    fragment SwiperItem_viewer on Viewer {
        ...LazyThumbVideo_viewer
    }
`;

const itemSearchFragment = graphql`
    fragment SwiperItem_itemSearch on ItemSearchQueryConnection {
        ...useValidFilter_itemSearch
    }
`;

export const SwiperItem: FC<Props> = ({
    viewer: viewerRef,
    photo: photoRef,
    itemSearch: itemSearchRef,
    itemId,
    index,
    useEagerImageLoad,
    containerClassName,
    disableLinks,
    onClick,
    pdpUrl,
    srcSetSizes,
    srcSetWidths,
    srcSetQuality,
    imageSize,
    title,
    imageLoadVerticalOffset,
    onContentLoaded,
    useLoFiLazyLoader,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef || null);
    const photo = useFragment(photoFragment, photoRef || null);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef || null);
    const validFilter = useValidFilter({ itemSearch });
    const photoAltText = useAppliedFilterAltText({ title, appliedFilter: validFilter });

    const swiperItem = useMemo(() => {
        let lazyImageProps: Partial<LazyImageProps> | undefined = undefined;
        let content = <Fragment />;

        if (photo) {
            lazyImageProps = getPhotoImgProps(photo, srcSetSizes, srcSetWidths, srcSetQuality);
            content = (
                <img
                    className={styles.swiperImage}
                    alt={photoAltText}
                    data-tn="product-image"
                    {...lazyImageProps.srcSetAttributes}
                    src={lazyImageProps.src}
                />
            );
        }

        // Use video thumbnail only if this is first item in swiper
        if (viewer && itemId) {
            content = (
                <LazyThumbVideo viewer={viewer} itemId={itemId} poster={lazyImageProps?.src}>
                    {content}
                </LazyThumbVideo>
            );
        }

        return useEagerImageLoad ? (
            content
        ) : (
            <Fragment>
                <LazyImage
                    className={styles.swiperImage}
                    alt={title}
                    offsetVertical={imageLoadVerticalOffset}
                    onImageLoad={onContentLoaded}
                    useLoFiLazyLoader={useLoFiLazyLoader}
                    imageSize={imageSize}
                    {...lazyImageProps}
                />
                {index === 0 && <noscript>{content}</noscript>}
            </Fragment>
        );
    }, [
        photo,
        viewer,
        index,
        itemId,
        useEagerImageLoad,
        title,
        imageLoadVerticalOffset,
        onContentLoaded,
        useLoFiLazyLoader,
        imageSize,
        srcSetSizes,
        srcSetWidths,
        srcSetQuality,
        photoAltText,
    ]);

    const swiperItemWithLink = (
        <SeoLink
            dataTn="thumb-image-link"
            linkData={{ path: pdpUrl, isLinkable: false }}
            onClick={onClick}
            className={styles.swiperLink}
        >
            <div className={containerClassName} data-tn="product-image-container">
                {swiperItem}
            </div>
        </SeoLink>
    );

    return disableLinks ? swiperItem : swiperItemWithLink;
};
